import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  Cpu,
  FileText,
  Wrench,
  MessageSquare,
  BookOpen,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  AlertTriangle,
} from "lucide-react";
import { useAuthStore } from "../store/authStore";
import { useSocketStore } from "../store/socketStore";
import clsx from "clsx";
import { ROLES } from "../constants/roles";

const navItems = [
  { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/machines", icon: Cpu, label: "Machines" },
  { path: "/sops", icon: FileText, label: "SOPs" },
  { path: "/maintenance", icon: Wrench, label: "Maintenance" },
  { path: "/assistant", icon: MessageSquare, label: "AI Assistant" },
  {
    path: "/knowledge",
    icon: BookOpen,
    label: "Knowledge Base",
    allowedRoles: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.TECHNICIAN],
  },
  {
    path: "/errors",
    icon: AlertTriangle,
    label: "Reports",
    allowedRoles: [
      ROLES.SUPER_ADMIN,
      ROLES.PLANT_MANAGER,
      ROLES.MACHINE_OPERATOR,
      ROLES.MAINTENANCE_TECHNICIAN,
    ],
  },
];

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuthStore();
  const { connect, disconnect } = useSocketStore();
  const navigate = useNavigate();
  const location = useLocation();
  const userRole = user?.role?.toLowerCase();

  useEffect(() => {
    const path = location.pathname.split("/")[1] || "dashboard";
    const matched = navItems.find((item) => item.path === `/${path}`);

    let title = "ConvoLink";
    if (matched) title = matched.label;
    else if (path && path !== "")
      title = path.charAt(0).toUpperCase() + path.slice(1);

    document.title = `${title} - ConvoLink`;
  }, [location.pathname]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) connect(token);
    return () => disconnect();
  }, [connect, disconnect]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const filteredNavItems = navItems.filter((item) => {
    if (!item.allowedRoles) return true;
    return item.allowedRoles.includes(userRole);
  });

  const NavItem = ({ item, isMobile = false }) => {
    const Component = NavLink;
    const props = isMobile ? { onClick: () => setMobileMenuOpen(false) } : {};

    return (
      <Component
        to={item.path}
        {...props}
        className={({ isActive }) =>
          clsx(
            "flex items-center gap-3 rounded-lg p-3 text-sm transition-colors",
            !sidebarOpen && !isMobile && "justify-center",
            isActive
              ? "bg-blue-500/10 text-blue-400 font-semibold"
              : "text-slate-400 hover:bg-slate-800 hover:text-white",
          )
        }
      >
        <item.icon className="h-5 w-5 flex-shrink-0" />
        {(sidebarOpen || isMobile) && <span>{item.label}</span>}
      </Component>
    );
  };

  return (
    <div className="h-screen flex overflow-hidden bg-white text-slate-800">
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/60 z-40 lg:hidden"
            />
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: "0%" }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.3, ease: "easeInOut" }}
              className="fixed inset-y-0 left-0 w-72 bg-slate-950 border-r border-slate-800 flex flex-col z-50"
            >
              <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800 flex-shrink-0">
                <img
                  src="/Machine-Interface-light.png"
                  alt="Logo"
                  className="h-12 w-auto object-contain"
                />
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 text-slate-400 rounded-full hover:bg-slate-800"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                {filteredNavItems.map((item) => (
                  <NavItem key={item.path} item={item} isMobile />
                ))}
              </nav>
              <div className="p-4 border-t border-slate-800">
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center gap-3 rounded-lg p-3 text-sm text-slate-400 hover:bg-red-500/10 hover:text-red-400"
                >
                  <LogOut className="h-5 w-5" />
                  <span>Logout</span>
                </button>
                <div className="mt-4 flex justify-start pl-3">
                  <img src="/PoweredBy.png" alt="Powered By" className="h-10" />
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <aside
        className={clsx(
          "hidden lg:flex flex-col bg-slate-950 border-r border-slate-800 transition-all duration-300",
          sidebarOpen ? "w-64" : "w-20",
        )}
      >
        <div
          className={clsx(
            "h-16 flex items-center border-b border-slate-800 px-4",
            sidebarOpen ? "justify-start" : "justify-center",
          )}
        >
          {sidebarOpen ? (
            <img
              src="/Machine-Interface-light.png"
              alt="Logo"
              className="h-12 w-auto object-contain"
            />
          ) : (
            <img
              src="/Machine-Interface-logo.png"
              alt="Logo Icon"
              className="h-8 w-auto"
            />
          )}
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {filteredNavItems.map((item) => (
            <NavItem key={item.path} item={item} />
          ))}
        </nav>
        <div className="p-4 border-t border-slate-800">
          <button
            onClick={handleLogout}
            className={clsx(
              "flex w-full items-center gap-3 rounded-lg p-3 text-sm text-slate-400 hover:bg-red-500/10 hover:text-red-400",
              !sidebarOpen && "justify-center",
            )}
          >
            <LogOut className="h-5 w-5" />
            {sidebarOpen && <span>Logout</span>}
          </button>
          {sidebarOpen && (
            <div className="mt-4 flex justify-start pl-3">
              <img src="/PoweredBy.png" alt="Powered By" className="h-10" />
            </div>
          )}
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 h-screen">
        <header className="h-16 bg-slate-950 border-b border-slate-800 flex items-center justify-between px-4 sm:px-6 flex-shrink-0">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 text-slate-400 rounded-full hover:bg-slate-800 lg:hidden"
            >
              <Menu className="w-5 h-5" />
            </button>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="hidden lg:inline-flex p-2 text-slate-400 rounded-full hover:bg-slate-800"
            >
              <ChevronLeft
                className={clsx(
                  "w-5 h-5 transition-transform duration-300",
                  !sidebarOpen && "rotate-180",
                )}
              />
            </button>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-semibold text-slate-100">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-slate-400 capitalize">
                {user?.role?.replace("_", " ")}
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-slate-300 font-bold text-sm border border-slate-600">
              {user?.firstName?.[0]}
              {user?.lastName?.[0]}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6 lg:p-8 bg-slate-50">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;